import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Stack, TextField } from "@mui/material";
import { StoreAccessEventPayload } from "../../../../services/types/accessEventTypes";
import { useStoreAccessEventMutation } from "../../../../services/cclTokenedAccessEventsApi";
import FlexGrid from "../../../../layouts/flexGrid";
import {
    useAddUpdateTargetRevenueMutation,
    useDeleteTargetRevenueMutation,
    useGetCeSystemUsersQuery,
    useSearchTargetRevenueMutation,
} from "../../../../services/cclTokenedCustomerHierarchyApi";
import {
    ClientSearchListConfigState,
    updateClientSearchParams,
} from "../../../../app/slices/clientSearchListConfigSlice";
import {
    AddTargetRevenue,
    TargetRevenueRequest,
    TargetRevenueResponse,
} from "../../../../services/types/clientTypes";
import CclErrorDialog from "../../../../components/common/cclErrorDialog";
import ComponentLoader from "../../../../components/common/componentLoader";
import CclSearchDefault from "../../../../components/common/cclSearchDefault";
import TargetRevenueDataGridPro from "./targetRevenueDataGridPro";
import AddTargetRevenueDialog from "./addTargetRevenueDialog";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";

interface TargetRevenuePanelProps {}

const TargetRevenuePanel: React.FC<TargetRevenuePanelProps> = () => {
    // redux state
    const dispatch = useDispatch();
    const { data: CESystemOwnerData } = useGetCeSystemUsersQuery();
    const config: ClientSearchListConfigState = useSelector(
        (state: any) => state.userSearchListConfig
    );
    const [searchFiscalYear, setSearchFiscalYear] = React.useState<number | undefined>(
        config.FiscalYear
    );
    const [fiscalSearchError, setFiscalSearchError] = React.useState<boolean>(false);
    const [searchAccountName, setSearchAccountName] = React.useState<string>(config.AccountName);
    /*   const [wildSearch, setWildSearch] = React.useState<boolean>(config.WildSearch);
    const [searchResultString, setSearchResultString] = React.useState<string>(""); */
    const [searchUsers, { data: targetRevenueData, isLoading, isSuccess, isUninitialized }] =
        useSearchTargetRevenueMutation();
    const [deleteData] = useDeleteTargetRevenueMutation();
    const [
        triggerCreateNewRevenue,
        { isLoading: addTargetRevenueLoading, isError: addTargetRevenueIsError },
    ] = useAddUpdateTargetRevenueMutation();
    const [showSearchToBroad, setShowSearchToBroad] = React.useState<boolean>(false);
    const [showAddtargetRevenueDialog, setShowAddtargetRevenueDialog] =
        React.useState<boolean>(false);
    const [showAlert, setShowAlert] = React.useState<boolean>(false);
    const [showSpinner, setShowSpinner] = React.useState<boolean>(false);
    const [storeAccessEvent] = useStoreAccessEventMutation();
    const [fiscalYearEdit, setfiscalYearEdit] = React.useState<number>();
    const [accountOwnerEdit, setAccountOwnerEdit] = React.useState<string>("");
    const [revenueTargetEdit, setRevenueTargetEdit] = React.useState<number>();
    const [fiscalMonthEdit, setFiscalMonthEdit] = React.useState<number>();
    const [addUpdateEdit, setAddUpdateEdit] = React.useState<boolean>(false);

    const [targetRevenueNewData, setTargetRevenueNewData] =
        React.useState<TargetRevenueResponse[]>();

    React.useEffect(() => {
        if (isSuccess && targetRevenueData && targetRevenueData.length >= 30000)
            setShowSearchToBroad(true);
        if (isSuccess) setTargetRevenueNewData(targetRevenueData);
    }, [targetRevenueData, isSuccess]);

    React.useEffect(() => {
        setShowSpinner(addTargetRevenueLoading);
    }, [addTargetRevenueLoading]);

    const getTargetRevenue = async () => {
        if (searchFiscalYear && searchFiscalYear === 0) {
            setFiscalSearchError(true);
            return;
        }
        setFiscalSearchError(false);
        const payload: TargetRevenueRequest = {
            fiscal_year: searchFiscalYear ?? 0,
            account_name: searchAccountName ?? "",
            wildcardSearch: true,
        };
        searchUsers(payload);
        dispatch(
            updateClientSearchParams({
                FiscalYear: payload.fiscal_year ?? 0,
                AccountName: payload.account_name ?? "",
                WildSearch: true,
            })
        );
    };

    const handleSearchKeyPress = async (evt: React.KeyboardEvent<HTMLDivElement>) => {
        if (evt.key === "Enter") {
            evt.preventDefault();
            getTargetRevenue();
        }
    };

    const addTargetRevenue = async (
        fiscalYear: number,
        ceSystem: string,
        revenueAmount: number,
        updateRevenueData: boolean,
        fiscalMonth: number
    ) => {
        var CEsystemID =
            CESystemOwnerData?.filter((c) => c.account_Name === ceSystem)[0].systemUserId ?? "";
        const payload: AddTargetRevenue = {
            fiscalYear: fiscalYear,
            cESystemID: CEsystemID,
            revenueValue: revenueAmount,
            updateData: updateRevenueData,
            fiscalMonth: fiscalMonth,
        };
        triggerCreateNewRevenue(payload)
            .unwrap()
            .then(() => {
                setShowAlert(true);
                setShowAddtargetRevenueDialog(false);
                setTargetRevenueNewData((prev) =>
                    prev?.map((a) =>
                        a.cesystemUserId !== CEsystemID
                            ? a
                            : {
                                  ...a,
                                  fiscal_year: fiscalYear,
                                  revenue: revenueAmount,
                                  fiscalMonth: fiscalMonth,
                              }
                    )
                );
                setfiscalYearEdit(0);
                setAccountOwnerEdit("");
                setRevenueTargetEdit(0);
                setFiscalMonthEdit(0);
                setAddUpdateEdit(false);

                const evtPayload: StoreAccessEventPayload = {
                    accessEventTypeId: 0,
                    accessEventData: JSON.stringify("Added Traget Revenue"),
                };
                storeAccessEvent(evtPayload);
            })
            .catch(() => {
                setShowAlert(true);
            });
    };

    const editTargetRevenue = async (targetRevenueID: number) => {
        var targetRevenueEdit = targetRevenueNewData?.filter(
            (c) => c.revenueId === targetRevenueID
        )[0];
        if (targetRevenueEdit) {
            setfiscalYearEdit(targetRevenueEdit?.fiscalYear);
            setAccountOwnerEdit(targetRevenueEdit?.accountOwnerName);
            setRevenueTargetEdit(targetRevenueEdit?.revenue);
            setFiscalMonthEdit(targetRevenueEdit?.fiscalMonth ?? 0);
            setAddUpdateEdit(true);
        }
        setShowAddtargetRevenueDialog(true);
    };

    const deleteTargetRevenue = async (targetRevenueID: number) => {
        deleteData(targetRevenueID);
        var targetRevenueEdit = targetRevenueNewData?.filter(
            (c) => c.revenueId !== targetRevenueID
        );
        setTargetRevenueNewData(targetRevenueEdit);
    };

    return (
        <Stack height={1} width={1}>
            {showSpinner && <ComponentLoader msg={"Updating"} />}
            <CclErrorDialog
                open={showSearchToBroad}
                title={"Too Many Results"}
                msg={
                    "There are too many results for this search. Consider adjusting your search parameters and searching again."
                }
                onOk={() => setShowSearchToBroad(false)}
            />

            <CclAlertSnackbar 
                open={showAlert} 
                message={addTargetRevenueIsError ? "Adding new target revenue failed" : "Target revenue added"} 
                severity={addTargetRevenueIsError ? "error" : "success"}
                onClose={function (): void {
                    setShowAlert(false);
                }}            
            />

            <AddTargetRevenueDialog
                open={showAddtargetRevenueDialog}
                onOk={addTargetRevenue}
                onCancel={() => {
                    setfiscalYearEdit(0);
                    setAccountOwnerEdit("");
                    setRevenueTargetEdit(0);
                    setFiscalMonthEdit(0);
                    setAddUpdateEdit(false);
                    setShowAddtargetRevenueDialog(false);
                }}
                CESystemData={CESystemOwnerData ?? []}
                accountOwnerValue={accountOwnerEdit}
                fiscalYearValue={fiscalYearEdit ?? 0}
                addUpdateValue={addUpdateEdit}
                revenueTargetValue={revenueTargetEdit ?? 0}
                fiscalMonthValue={fiscalMonthEdit ?? 0}
            />

            <Grid item xs={12} md={5}>
                <Box
                    sx={{
                        m: 2,
                        width: 1,
                    }}
                >
                    <Grid container component="form" sx={{alignItems: "center"}}>
                        <Grid item xs={6} md={5} sx={{ p: 1 }}>
                            <TextField
                                value={searchFiscalYear}
                                sx={{ width: 1, maxHeight: "57px" }}
                                id="outlined-adornment-email-search"
                                label="Enter Fiscal Year"
                                variant="outlined"
                                error={fiscalSearchError}
                                helperText={
                                    fiscalSearchError
                                        ? "Target Revenue should contain only numbers"
                                        : ""
                                }
                                onChange={(e) => {
                                    e.preventDefault();
                                    e.target.value.replace(/\D/g, "") !== ""
                                        ? setSearchFiscalYear(parseInt(e.target.value))
                                        : setSearchFiscalYear(undefined);
                                    e.target.value === ""
                                        ? setFiscalSearchError(false)
                                        : setFiscalSearchError(
                                              e.target.value.replace(/\D/g, "") === ""
                                          );
                                }}
                                onKeyPress={(e) => handleSearchKeyPress(e)}
                            />
                        </Grid>
                        <Grid item xs={6} md={5} sx={{ p: 1 }}>
                            <TextField
                                value={searchAccountName}
                                sx={{ width: 1, maxHeight: "57px" }}
                                id="outlined-adornment-email-search"
                                label="Enter Account Name"
                                variant="outlined"
                                onChange={(e) => {
                                    e.preventDefault();
                                    setSearchAccountName(e.target.value);
                                }}
                                onKeyPress={(e) => handleSearchKeyPress(e)}
                            />
                        </Grid>
                        <Grid item xs={6} md={1} sx={{ p: 1 }}> 
                            <CclButton
                                onClick={() => getTargetRevenue()}
                                aria-label="Search by Email"
                                size='small'
                                sx={{ width: 1, height: 1, maxHeight: "57px", marginLeft: "10px" }}
                                mode={"primary"} >
                                Search
                            </CclButton>
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
            <Grid item xs={12} md={5}>
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
                    <CclButton
                        onClick={(evt) => {
                            evt.stopPropagation();
                            setShowAddtargetRevenueDialog(true);
                            setAddUpdateEdit(false);
                        } }
                        sx={{
                            m: 1,
                            mr: 2,        
                            padding: "5px",
                            float: "right",
                        }} mode={"primary"}>
                        Add New Target Revenue
                    </CclButton>
                </Stack>
            </Grid>
            <Box
                sx={{
                    width: 1,
                    height: 1,
                    m: 2,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {isLoading ? (
                    <ComponentLoader msg="Loading" />
                ) : isUninitialized ? (
                    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        <CclSearchDefault
                            line1="Start searching for Target Revenue"
                            line2="Enter search criteria and click Search"
                            hasButtons={false}
                        />
                    </Box>
                ) : (
                    <FlexGrid>
                        <TargetRevenueDataGridPro
                            targetRevenueList={targetRevenueNewData ?? []}
                            isLoading={isLoading}
                            showToolbar={!isLoading}
                            OnTargetRevenueDelete={deleteTargetRevenue}
                            OnTargetRevenueEdit={editTargetRevenue}
                        />
                    </FlexGrid>
                )}
            </Box>
        </Stack>
    );
};

export default TargetRevenuePanel;
